import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Moon, HeartPulse, Scale, Gauge, Lightbulb, X, Loader2 } from 'lucide-react';
import { AuthContext, TasksContext } from '@/App';
import ProBadge from '@/components/ProBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AREA = {
  sleep: { icon: Moon, color: '#a78bfa', label: 'Sleep' },
  health: { icon: HeartPulse, color: '#f472b6', label: 'Health' },
  balance: { icon: Scale, color: '#34d399', label: 'Balance' },
  workload: { icon: Gauge, color: '#fbbf24', label: 'Workload' },
};

const SuggestionsPanel = () => {
  const { auth } = useContext(AuthContext);
  const { tasksVersion, bumpTasks } = useContext(TasksContext);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const headers = { Authorization: `Bearer ${auth.token}` };
  const tz = -new Date().getTimezoneOffset();

  useEffect(() => {
    axios.get(`${API}/suggestions`, { headers, params: { tz_offset_minutes: tz } }).then(r => setItems(r.data || [])).catch(() => {});
  }, [tasksVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async (s) => {
    if (s.locked) { navigate('/settings'); return; }
    setBusy(s.id);
    try {
      await axios.post(`${API}/suggestions/${encodeURIComponent(s.id)}/apply`, { tz_offset_minutes: tz }, { headers });
      toast.success(`Done: ${s.title}`);
      bumpTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Could not apply');
    } finally { setBusy(null); }
  };

  const dismiss = async (s) => {
    setItems(prev => prev.filter(x => x.id !== s.id));
    axios.post(`${API}/suggestions/${encodeURIComponent(s.id)}/dismiss`, {}, { headers }).catch(() => {});
  };

  if (!items.length) return null;
  return (
    <div className="mb-6" data-testid="suggestions-panel">
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb size={15} className="text-amber-300"/>
        <h2 className="text-sm font-semibold text-white">SpaceCal suggests</h2>
        <span className="text-[11px] text-gray-500">based on your patterns</span>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {items.map((s, i) => {
          const a = AREA[s.area] || AREA.balance;
          const Icon = a.icon;
          return (
            <div key={s.id} className="glass-card p-4 flex gap-3 animate-in fade-in slide-in-from-bottom-2" style={{ animationDelay: `${i * 60}ms` }} data-testid={`suggestion-${i}`}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${a.color}18`, border: `1px solid ${a.color}33` }}>
                <Icon size={15} style={{ color: a.color }}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: a.color }}>{a.label}</span>
                  {s.pro && <ProBadge testId={`suggestion-pro-badge-${i}`}/>}
                </div>
                <p className="text-sm font-medium text-white" data-testid={`suggestion-title-${i}`}>{s.title}</p>
                <p className="text-xs text-gray-400 mt-0.5">{s.detail}</p>
                {s.action && (
                  <button onClick={() => apply(s)} disabled={busy === s.id} data-testid={`suggestion-apply-${i}`}
                    className="mt-2 text-xs px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1 transition-colors"
                    style={{ background: `${a.color}22`, color: a.color, border: `1px solid ${a.color}44` }}>
                    {busy === s.id && <Loader2 size={11} className="animate-spin"/>}
                    {s.locked ? 'Upgrade to apply' : 'Apply'}
                  </button>
                )}
              </div>
              <button onClick={() => dismiss(s)} data-testid={`suggestion-dismiss-${i}`} className="self-start p-1 text-gray-600 hover:text-gray-300"><X size={13}/></button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SuggestionsPanel;
